import { router } from "@inertiajs/react";
import { Card, SimpleGrid, Title } from "@mantine/core";
import { modals } from "@mantine/modals";
import { ApprenticeViewTabs, ApprenticeViewTabValues } from "../../../components/apprentice/ApprenticeViewTabs";
import { DocumentCard } from "../../../components/card/DocumentCard";
import { ReportCard } from "../../../components/card/ReportCard";
import { SignModal } from "../../../components/modals/SignModal";
import { openFile } from "../../../helpers/fileHelper";
import { getFullName } from "../../../helpers/stringHelper";
import { useRole } from "../../../hooks/useProps";
import { useYearContext } from "../../../hooks/useYearContext";
import { ContentShell } from "../../../layout/ContentShell";
import type { Apprentice } from "../../../models/apprentice";
import type { Document } from "../../../models/document";
import { selfAssesmentReportTypeRoute } from "../../../models/enums/reportType";
import { Role } from "../../../models/enums/role";
import type { Report } from "../../../models/reports/report";
import { apprentice_document_path, apprentices_path, document_views_path } from "../../../routes";

type ApprenticeDocumentsProps = {
  documents: Document[];
  self_assess_reports: Report[];
  apprentice: Apprentice;
};

export default function ApprenticeDocuments({ documents, apprentice, self_assess_reports }: ApprenticeDocumentsProps) {
  const { pathWithYearContext } = useYearContext();
  const role = useRole();
  const onBack = role !== Role.APPRENTICE ? () => router.get(pathWithYearContext(apprentices_path)) : undefined;
  const createDocumentView = (documentId: number, signed = false) => {
    if (role && ![Role.ADMIN, Role.COACH].includes(role)) {
      router.post(pathWithYearContext(document_views_path), { document_id: documentId, signed });
    }
  };

  const onDocumentClick = (document: Document) => {
    createDocumentView(document.id);
    openFile(pathWithYearContext(apprentice_document_path, apprentice.id, document.id));
  };

  const openModal = (document: Document) => openSignModal(document, () => createDocumentView(document.id, true));

  const onAssess = (report: Report) => {
    const route = selfAssesmentReportTypeRoute[report.report_configuration.report_type];

    if (route) {
      router.get(pathWithYearContext(route, apprentice.id, report.id));
    }
  };

  const documentCards = documents.map((document) => (
    <DocumentCard
      key={`${document.id}-document`}
      shadow="xs"
      padding="lg"
      withBorder
      onOpen={() => onDocumentClick(document)}
      document={document}
      onSign={document.signable ? () => openModal(document) : undefined}
    />
  ));

  const selfAssessReportCards = self_assess_reports.map((report) => (
    <ReportCard key={`${report.id}-report`} shadow="xs" padding="lg" withBorder report={report} onAssess={onAssess} />
  ));

  const cards = [...selfAssessReportCards, ...documentCards];

  return (
    <ContentShell title={`Dokumente: ${getFullName(apprentice)}`} pageTitle={`Dokumente - ${getFullName(apprentice)}`} onBack={onBack}>
      <ApprenticeViewTabs apprenticeId={apprentice.id} activeTab={ApprenticeViewTabValues.DOCUMENTS} />

      <SimpleGrid cols={{ base: 1, md: 2, lg: 3, xl: 3 }}>
        {cards.length === 0 && (
          <Card shadow="xs" padding="lg" withBorder>
            <Title order={4}>Keine Dokumente...</Title>
          </Card>
        )}
        {cards}
      </SimpleGrid>
    </ContentShell>
  );
}

export const openSignModal = (document: Document, onConfirm: () => void) =>
  modals.open({
    withCloseButton: false,
    size: "lg",
    children: (
      <SignModal
        onConfirm={() => {
          onConfirm();
          modals.closeAll();
        }}
        documentTitle={document.title}
        onClose={() => modals.closeAll()}
        disabled={document.signed}
      />
    ),
  });
